import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent, ButtonClass } from '@claas/claas-ui-components';
import { HeaderComponent } from '@claas/claas-ui-components/navigation/header';
import { TranslateModule } from '@ngx-translate/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { combineLatest, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { RouterOutlet } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ApiPartnerEnum } from '@claas-rdv/rdv-client';
import { AuthWrapperService } from './services/auth-wrapper.service';

// Locale registration and LOCALE_ID live in locale-registry.ts / app.config.ts,
// where the resolved language is available before bootstrap.
@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslateModule, HeaderComponent, RouterOutlet],
})
export class App {
  private authWrapperService = inject(AuthWrapperService);
  private oktaAuthStateService = inject(OktaAuthStateService);
  private auth0AuthService = inject(AuthService);

  /**
   * True if the user is authenticated via Okta OR Auth0.
   * Starts with false immediately so the buttons are visible on first render.
   * Buttons are hidden as soon as either provider confirms a session.
   */
  isLoggedIn$: Observable<boolean> = combineLatest([
    // Okta: emit false immediately, then track real auth state
    this.oktaAuthStateService.authState$.pipe(
      map((s) => !!s.isAuthenticated),
      startWith(false),
    ),
    // Auth0: wait for loading to finish, then emit; start with false
    this.auth0AuthService.isLoading$.pipe(
      filter((loading) => !loading),
      switchMap(() => this.auth0AuthService.isAuthenticated$),
      startWith(false),
    ),
  ]).pipe(map(([okta, auth0]) => okta || auth0));

  expanded = signal(false);
  protected readonly buttonClass = ButtonClass;

  public async signInAsDealer(): Promise<void> {
    await this.authWrapperService.loginWithRedirect(ApiPartnerEnum.SOFF);
  }

  public async signInAsCustomer(): Promise<void> {
    await this.authWrapperService.loginWithRedirect(ApiPartnerEnum.CC3);
  }
}
